<!-- All media content in SLS will be rendered using Media component, be it UGC, MediaObject, Embed App / Website, Youtube -->
<template>
  <div
    :id="index"
    ref="rootEl"
    class="media-component"
    :class="{ 'transcript-enabled': showTranscript }"
  >
    <div v-if="!visible" ref="mediaElement" :class="lazyLoadMediaCss" />
    <figure
      v-else
      v-show="componentVisibility"
      class="media"
      :class="mediaCss"
      :data-type="type"
      :data-media-type="mediaType"
      :data-uuid="mediaUuid"
      :data-cue-start="cueStart"
      :data-cue-end="cueEnd"
      :data-strict="strictModeLocal"
      :data-baseurl="youtubeSrcLocal"
      :data-title="dataTitle"
      :data-url="dataUrl"
    >
      <inline-loader v-if="isLoading || isMediaFileInUploading" />

      <div
        v-else-if="showEmptyPlaceholder"
        class="media-preview status-message"
        :class="{ 'not-ready': !isMediaFileReady, 'not-available': isMediaFileNotAccessible }"
      >
        <empty-placeholder class="info">
          <template #icon>
            <EmptyImage />
          </template>
          {{ statusMessage }}
        </empty-placeholder>
      </div>
      <div v-else-if="isMediaFileReady" class="media-preview">
        <image-annotation-canvas
          v-if="isImage && isEditingImageAnnotation"
          :image-url="mediaUrl"
          :anchor="mediaAnchor"
          :index="index"
          :media-title="mediaTitle"
          :media-uuid="uuid"
          :event-bus="imageAnnotationEventBus"
          :stamp-icons-selection="stampIconSelection"
          :annotation-resource-id="componentContext?.componentId"
          :max-width="mediaComponentWidth || undefined"
          @done="handleDoneAddingImageAnnotation"
        />
        <!-- TODO: support click to load -->
        <image-view
          v-if="isImage"
          v-show="!isEditingImageAnnotation"
          ref="image"
          :media="imageData"
          :annotatable="annotatable"
          :stamp-icons-selection="stampIconSelection"
          :component-id="componentContext?.componentId"
          :annotation="annotation"
          :show-image-annotation="showImageAnnotation"
          :max-width="mediaComponentWidth || undefined"
          @media-loaded="mediaLoaded"
          @media-visible="mediaVisible"
          @click-image="clickImage"
          @double-click-image="emit('doubleClickImage')"
        />
        <media-player
          v-else-if="isVideo || isAudio"
          ref="player"
          :media="mediaPlayerData"
          :caption-src="captionSrc"
          :is-transcript-enabled="isTranscriptEnabledLocal"
          :show-transcript="showTranscript"
          :is-from-subpage="isFromSubpage"
          :language="language"
          :annotatable="annotatable"
          :anchor="mediaAnchor"
          :transcript-start-time="transcriptStartTime ?? undefined"
          :render-annotation="!isEmpty(props.annotationAnchor)"
          @media-loaded="mediaLoaded"
          @media-playable="handleMediaPlayable"
          @media-ended="handleMediaEnded"
          @media-paused="handleMediaPaused"
        />
        <youtube-player
          v-else-if="isYoutube"
          ref="player"
          :media="youtubePlayerData"
          @media-loaded="mediaLoaded"
        />
        <frame-view
          v-else-if="isHTML5 || isEmbed"
          :media="frameData"
          :is-h-t-m-l5="isHTML5"
          :context="context"
          @media-loaded="mediaLoaded"
          @media-invalid="mediaInvalid"
          @media-not-iframeable="mediaNotIframeable"
        />
      </div>

      <div v-if="showExternalSourceBanner" class="external-source-banner">
        <span class="icon"><svg-icon name="Copyright24" kind="action" /></span>
        <span :data-text="copyrightMessage" />
      </div>

      <div v-if="showExternalSourceBanner && showCopyrightDisclaimer" class="copyright-disclaimer">
        <span :data-text="copyrightDisclaimer" />
      </div>

      <div v-if="showDrawingRestrictionBanner" class="drawing-restriction-banner">
        <span :data-text="drawingRestrictionMessage" />
        <cv-tooltip
          v-if="showDrawingRestrictionTooltip"
          class="info-tooltip"
          alignment="end"
          direction="top"
          :tip="drawingRestrictionTooltipMessage"
        >
          <svg-icon name="Info24" kind="action" />
        </cv-tooltip>
      </div>

      <image-annotation-toolbar
        v-if="isEditingImageAnnotation"
        :dirty-id="`image-annotation-${mediaAnchor}`"
        :event-bus="imageAnnotationEventBus"
        @back="handleDoneAddingImageAnnotation"
      />

      <!-- TODO: handling for cuepoints message, handling of disclaimer -->
      <attachment-placeholder
        v-if="!isMediaFileNotAccessible && showCaption && !showCopyrightPlaceHolder"
        v-show="!isEditingImageAnnotation"
        :class="cssClasses"
        :media-title="mediaTitle"
        :media-type="mediaType"
        :is-inline="isInline"
        :show-title="showTitle"
        :icon-type="iconType('MEDIA', mediaType, false)"
      >
        <template v-if="showZoomTools" #zoom-tools>
          <button class="zoom-in" type="button">
            <cv-tooltip
              alignment="start"
              direction="bottom"
              :tip="t('cropping_action_button_zoom_in')"
            >
              <svg-icon name="ZoomIn24" kind="action" />
            </cv-tooltip>
          </button>
          <button class="zoom-out" type="button">
            <cv-tooltip
              alignment="start"
              direction="bottom"
              :tip="t('cropping_action_button_zoom_out')"
            >
              <svg-icon name="ZoomOut24" kind="action" />
            </cv-tooltip>
          </button>
          <button class="fit-to-height" type="button">
            <cv-tooltip
              alignment="start"
              direction="bottom"
              :tip="t('cropping_action_button_fit_to_height')"
            >
              <svg-icon name="FitToHeight24" kind="action" />
            </cv-tooltip>
          </button>
          <button class="fit-to-width" type="button">
            <cv-tooltip
              alignment="start"
              direction="bottom"
              :tip="t('cropping_action_button_fit_to_width')"
            >
              <svg-icon name="FitToWidth24" kind="action" />
            </cv-tooltip>
          </button>
        </template>
        <template v-if="showActionTools" #action-tools>
          <div class="action-tools">
            <!-- show only if isMO and lesson is in edit mode - controlled by css-->
            <star-resource-button
              v-if="showStarButton"
              type="button"
              :resource-uuid="uuid"
              resource-type="MEDIA"
            />

            <button
              v-if="showAskSalisButton"
              class="btn-ask-salis action"
              :disabled="
                !isAskSalisSupportedComponent ||
                hasReachedAskSalisInteractionLimits ||
                isConversationSuspended
              "
              @click="handleAskSalis"
            >
              <cv-tooltip alignment="end" direction="bottom" :tip="askSalisTooltipMessage">
                <svg-icon name="Salis24" kind="action" />
              </cv-tooltip>
            </button>

            <button v-if="showMarkCopy" class="btn-mark-copy action" @click="handleMarkCopy">
              <cv-tooltip alignment="end" direction="bottom" tip="">
                <svg-icon :name="getMarkCopyIconName" kind="action" />
              </cv-tooltip>
            </button>

            <button
              v-if="showImageAnnotationToggle"
              class="note action"
              type="button"
              @click="handleAddImageAnnotation()"
            >
              <cv-tooltip alignment="end" direction="bottom" tip="">
                <svg-icon name="AnnotateImage24" kind="action" />
              </cv-tooltip>
            </button>
            <button
              v-if="showTimestampAnnotationToggle"
              class="timestamp action"
              :class="{ _active: isAnnotatable }"
              type="button"
              @click="handleTimestampAnnotationToggle()"
            >
              <cv-tooltip alignment="end" direction="bottom" tip="">
                <svg-icon name="Annotate24" kind="action" />
              </cv-tooltip>
            </button>
            <button v-if="showDrawing" class="drawing action" @click="handleDraw">
              <cv-tooltip alignment="end" direction="bottom" tip="">
                <svg-icon name="EditImage24" kind="action" />
              </cv-tooltip>
            </button>
            <button
              v-if="showRestriction"
              class="restriction action"
              type="button"
              @click="handleRestrictionToggle"
            >
              <svg-icon name="Restriction24" kind="action" />
            </button>
            <button
              v-if="showDownload"
              type="button"
              class="download action"
              @click="handleDownload"
            >
              <cv-tooltip alignment="end" direction="bottom" :tip="t('page_title_download')">
                <svg-icon name="Download24" kind="action" />
              </cv-tooltip>
            </button>
            <!-- TODO: FE dev to replace class for showOpenHtml5SubpageButton button -->
            <button
              v-if="showOpenHtml5SubpageButton"
              type="button"
              class="open-in-new-tab action"
              @click="handleOpenHtml5Subpage"
            >
              <cv-tooltip
                alignment="end"
                direction="bottom"
                :tip="t('authoring_copilot_button_tooltip')"
              >
                <svg-icon name="ACPRegenerate24" kind="action" />
              </cv-tooltip>
            </button>
            <button
              v-if="showOpenInNewTab"
              type="button"
              class="open-in-new-tab action"
              @click="handleOpenInNewTab"
            >
              <cv-tooltip
                alignment="end"
                direction="bottom"
                :tip="t('media_object_open_in_new_tab_btn')"
              >
                <svg-icon name="ExternalLink24" kind="action" />
              </cv-tooltip>
            </button>
            <button
              v-if="showOpenPdfOverlay"
              type="button"
              class="open-pdf-overlay action"
              @click="handleOpenPdfOverlayClick"
            >
              <cv-tooltip alignment="end" direction="bottom" :tip="t('media_object_details_btn')">
                <svg-icon name="Details24" kind="action" />
              </cv-tooltip>
            </button>
            <button
              v-if="showTrash"
              type="button"
              class="trash action"
              :disabled="disableMedia"
              @click="handleRemove"
            >
              <cv-tooltip alignment="end" direction="bottom" :tip="t('media_object_delete_btn')">
                <svg-icon name="Trash24" kind="action" />
              </cv-tooltip>
            </button>
            <button
              v-if="showSettings"
              type="button"
              class="settings action"
              :disabled="disableMedia"
              @click.stop="handleSettings"
            >
              <cv-tooltip
                alignment="end"
                direction="bottom"
                :tip="t('media_object_view_settings_btn')"
              >
                <svg-icon name="Settings24" kind="action" />
              </cv-tooltip>
            </button>
            <cv-tooltip
              v-if="showAiInfoIcon"
              type="button"
              class="ai-attribution"
              alignment="end"
              direction="bottom"
            >
              <svg-icon name="Info24" kind="action" />
              <template #content>
                <template v-if="hasMoeCopyrights">
                  <h6 class="title">{{ t("media_object_copyright_label") }}</h6>
                  <p class="description">
                    <template v-for="line in copyrightTooltipLines" :key="line.text">
                      <span>{{ line.text }}</span>
                      <br v-if="line.showLineBreak" />
                    </template>
                  </p>
                </template>
                <h6 class="title">{{ t("media_object_ai_attribution_label") }}</h6>
                <p class="description">{{ aiAttributionDescription }}</p>
              </template>
            </cv-tooltip>
            <cv-tooltip
              v-if="showCopyrightIcon"
              type="button"
              class="copyright"
              alignment="end"
              direction="bottom"
            >
              <svg-icon name="Copyright24" kind="action" />
              <template #content>
                <h6 class="title">{{ t("media_object_copyright_label") }}</h6>
                <p class="description">
                  <template v-for="line in copyrightTooltipLines" :key="line.text">
                    <span>{{ line.text }}</span>
                    <br v-if="line.showLineBreak" />
                  </template>
                </p>
              </template>
            </cv-tooltip>
          </div>
        </template>

        <template v-if="showEmbedUrl" #embed-url>
          <div class="embed-url">
            <span v-if="isEmbed"> {{ embedData?.frameAttributes.src }} </span>
            <span v-if="isYoutube"> {{ youtubeSrcLocal }} </span>
          </div>
        </template>
      </attachment-placeholder>
      <media-transcription
        v-if="!isLoading && (isVideo || isAudio) && showTranscript"
        ref="transcription"
        :caption-src="captionSrc"
        :is-video="isVideo"
        :can-edit-transcript="false"
        :transcript-start="transcriptStart ?? undefined"
        :transcript-end="transcriptEnd ?? undefined"
        :annotatable="annotatable"
        :media-uuid="mediaUuid"
        :anchor="mediaAnchor"
        :show-highlighted-cues-only="printView"
        :show-highlight-on-media="true"
        :annotation-uuid="annotationUuid ?? undefined"
        :is-from-subpage="isFromSubpage"
        @track-loaded="handleTrackLoaded"
      />
      <annotatable-rich-text
        v-if="hasSEText"
        class="se-text"
        :anchor="anchor"
        :can-annotate="false"
        :content="seText"
        :is-s-e-rich-text="true"
        :parent-anchor="parentAnchor"
        :parent-question-context="parentQuestionContext"
      />
    </figure>
    <message-component v-if="showUnapprovedMOErrorLabel" class="error submit-validation-error">
      {{ unnapprovedMOErrorLabel }}
    </message-component>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
  defineAsyncComponent,
  inject,
} from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import DOMUtils from "@utils/dom.utils";
import MediaObjectApi from "@apis/media.object.api";
import ugcApi from "@apis/ugc.api";
import {
  MEDIA_TYPE,
  MEDIA_FILE_STATUS,
  MO_FETCH_INTERVAL,
  MO_DEFAULT_FETCH_INTERVAL,
  MEDIA_OBJECT_COPYRIGHT_OWNER,
  HTML5_ACTIVITY_ID_PREFIX,
  AVAILABILITY,
  RESOURCE_STATUS,
  HTML_TOKEN_LAST_FETCH_STORAGE_KEY,
  HTML_TOKEN_STORAGE_KEY,
  LICENSE_TYPE,
} from "../media.constant";
import { isDefined, isNull, isEmpty, notNull } from "@utils/var.check";
import MediaAccessRestriction from "./MediaAccessRestriction.vue";
import submitLessonValidationStore from "@modules/editor/components/lesson/SubmitLessonValidationStore";
import { MediaPlayerEventBus } from "@plugins/media/service/media.player.event.bus";
import ROLE from "@configs/role";
import mediaEventBus from "@plugins/media/service/event-bus.ts";
import { AnnotationEventBus } from "@modules/annotation/service/annotation.event.bus";
import EventBus from "@plugins/common/service/eventbus";
import appConfigs from "@configs/app";
import { SpeechEvaluationEventBus } from "@plugins/richtext/plugins/speech-evaluation/service/event-bus";
import { getSeReferenceText } from "@plugins/richtext/plugins/speech-evaluation/service/se-reference.service";
import { NAMESPACE, RESOURCE_DOMAIN } from "@modules/attempt/service/attempt.constant";
import { NAMESPACE as EDITOR_NAMESPACE } from "@modules/editor/service/editor.constant";
import { ERROR_CODE } from "@plugins/common/constant/error.constant";
import AcpHtml5InteractiveIterationContentSubpage from "@plugins/authoring-copilot/components/AcpHtml5InteractiveIterationContentSubpage.vue";
import { AcpHtml5Service } from "@plugins/richtext/plugins/authoring-copilot/acp.html5.services.ts";
import { Html5ListItem } from "@plugins/authoring-copilot/types.ts";
import {
  ACP_HTML5_TYPE,
  DRAWER_CONTENT_TYPE,
  DRAWER_NAME,
} from "@modules/app/service/app.constant.ts";
import richTextUtils from "@plugins/richtext/utils/richtext.utils.ts";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";
import { useToast } from "@plugins/toast/composables/toast-instance.ts";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";
import { useEventBusWatch } from "@plugins/common/composables/event-bus-watch.ts";
import { useResource } from "@plugins/common/composables/use-resource.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { useRoute } from "vue-router";
import { ApiResponse } from "@apis/api.ts";
import MessageComponent from "@plugins/common/components/MessageComponent.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import StarResourceButton from "@plugins/common/components/StarResourceButton.vue";
import EmptyPlaceholder from "@plugins/common/components/EmptyPlaceholder.vue";
import InlineLoader from "@plugins/common/components/InlineLoader.vue";
import {
  CuePoints,
  EmbedConfig,
  Context,
  MediaMetadata,
  MediaPlayablePayload,
  TimeStampTriggerPayload,
  EditEmbedPayload,
  YouTubeSettingsPayload,
  MediaSettingsPayload,
  MediaInput,
  FrameAttributes,
  MediaData,
} from "@plugins/media/types.ts";
import AttachmentPlaceholder from "@plugins/common/components/AttachmentPlaceholder.vue";
import ImageAnnotationToolbar from "@modules/annotation/components/ImageAnnotationToolbar.vue";
import { performFileDownload } from "@utils/download.utils.ts";
import { appLocalStorage } from "@utils/app.storage";
import TokenApi from "@apis/token.api.ts";
import {
  isHtmlSignedCookieClearedError,
  isMissingApplicationJwtError,
  useHtmlSignedCookie,
} from "@plugins/common/composables/use-html-signed-cookie.ts";
import {
  PDF_VIEWER_OPEN_KEY,
  type PdfViewerOpenFn,
} from "@plugins/pdf-viewer/composables/use-pdf-viewer-open.ts";
import { ConversationDrawerEventBus } from "@modules/conversation/services/conversation.drawer.event.bus.ts";
import { usePageDrawer } from "@modules/app/composables/page.drawer.ts";
import { useLessonActivityConversation } from "@modules/attempt/composables/use-lesson-activity-conversation.ts";
import { isMediaObjectFileReady } from "@plugins/media/media.utils.ts";
import { useAttemptSpace } from "@modules/attempt/composables/use-attempt-space.ts";
import ImageAnnotationCanvas from "@modules/annotation/components/ImageAnnotationCanvas.vue";
import { AnnotationApiModel } from "@modules/annotation/types.ts";

defineOptions({ inheritAttrs: false });

interface TooltipLine {
  text: string;
  showLineBreak: boolean;
}

const YoutubePlayer = defineAsyncComponent(() => import("./YouTubePlayer.vue"));
const MediaPlayer = defineAsyncComponent(() => import("./MediaPlayer.vue"));
const ImageView = defineAsyncComponent(() => import("./MediaImageView.vue"));
const FrameView = defineAsyncComponent(() => import("./MediaFrameView.vue"));
const EmptyImage = defineAsyncComponent(
  () => import("@images/placeholder/empty-states/EmptyImage.svg?component"),
);
const MediaTranscription = defineAsyncComponent(() => import("./MediaTranscription.vue"));
const AnnotatableRichText = defineAsyncComponent(
  () => import("@modules/annotation/components/AnnotatableRichText.vue"),
);

const questionContext = inject("questionContext") as { uuid?: string } | undefined;
const resourceContext = inject("resourceContext") as { resourceUuid?: string } | undefined;
const componentContext = inject("componentContext") as { componentId?: string } | undefined;
const pdfViewerOpenFn = inject<PdfViewerOpenFn | null>(PDF_VIEWER_OPEN_KEY, null);

const props = withDefaults(
  defineProps<{
    uuid?: string;
    type?: string;
    mediatype?: string;
    altText?: string;
    mediaSrc?: string;
    youtubeSrc?: string;
    context?: Context;
    width?: string;
    height?: string;
    cuePoints?: CuePoints;
    copyrightProtected?: boolean;
    lazyload?: boolean;
    caption?: boolean;
    actionTools?: boolean;
    zoomTools?: boolean;
    showTitle?: boolean;
    openInNewTab?: boolean;

    isUgc?: boolean;
    isExternal?: boolean;
    resourceType?: string;
    isInline?: boolean;
    index?: string;
    imageRescale?: boolean;
    annotatable?: boolean;
    canMarkCopy?: boolean;
    embedConfig?: EmbedConfig | null;
    resourceDomain?: string | null;
    showUnapprovedMOError?: boolean;
    componentVisibility?: boolean;
    printView?: boolean;
    showAiInfoIcon?: boolean;
    copyright?: boolean;
    restriction?: boolean;
    fromMediaSettings?: boolean;
    dataDisplayStart?: string | null;
    hideControls?: boolean;
    strictMode?: boolean;
    allowSeekOnPaused?: boolean;
    isSplittable?: boolean;
    isComment?: boolean;
    isTranscriptEnabled?: boolean;
    transcriptionFileVersion?: string | null;
    renderTranscript?: boolean;
    isFromSubpage?: boolean;
    parentAnchor?: string | null;
    annotationAnchor?: string | null;
    hasSEAudio?: boolean;
    isSEGraded?: boolean;
    html5InteractiveConfig?: { questionId?: string; html5MOUuid?: string } | null;
    transcriptStartTime?: number | null;
    parentQuestionContext?: unknown[];
    questionAttemptUuid?: string;
    commentContext?: { resourceType?: string; resourceUuid?: string } | null;
    isAddNewMedia?: boolean;
    editor?: { options: { get: (k: string) => unknown }; save: () => void } | null;
    hideStarButton?: boolean;
    annotationUuid?: string | null;
    isUnavailable?: boolean;
    showDrawingRestriction?: boolean;
    // eslint-disable-next-line vue/prop-name-casing
    dynamicMountChildMixin_parentComponent?: unknown;
    // eslint-disable-next-line vue/prop-name-casing
    dynamicMountChildMixin_isDynamicallyMounted?: boolean;
    canAskSalis?: boolean;
    isAskSalisSupportedComponent?: boolean;
    showTrashOverride?: boolean;
    disableMedia?: boolean;
    showSettingsOverride?: boolean | null;
    showDownloadOverride?: boolean | null;
    showImageAnnotation?: boolean;
    annotation?: AnnotationApiModel;
  }>(),
  {
    uuid: "",
    type: "",
    mediatype: "",
    altText: "",
    mediaSrc: "",
    youtubeSrc: "",
    context: undefined,
    width: "",
    height: "",
    cuePoints: () => ({}),
    copyrightProtected: false,
    lazyload: true,
    caption: true,
    actionTools: true,
    zoomTools: false,
    showTitle: true,
    openInNewTab: true,
    resourceType: "",
    isInline: false,
    index: "",
    imageRescale: true,
    annotatable: false,
    canMarkCopy: false,
    embedConfig: null,
    resourceDomain: null,
    showUnapprovedMOError: false,
    componentVisibility: true,
    printView: false,
    showAiInfoIcon: true,
    copyright: true,
    restriction: true,
    fromMediaSettings: false,
    dataDisplayStart: null,
    hideControls: true,
    strictMode: false,
    allowSeekOnPaused: false,
    isSplittable: false,
    isComment: false,
    isTranscriptEnabled: false,
    transcriptionFileVersion: null,
    renderTranscript: false,
    isFromSubpage: false,
    parentAnchor: null,
    annotationAnchor: null,
    hasSEAudio: false,
    isSEGraded: false,
    html5InteractiveConfig: null,
    transcriptStartTime: null,
    parentQuestionContext: () => [],
    questionAttemptUuid: "",
    commentContext: null,
    isAddNewMedia: false,
    editor: null,
    hideStarButton: false,
    annotationUuid: null,
    isUnavailable: false,
    showDrawingRestriction: false,
    dynamicMountChildMixin_parentComponent: null,
    dynamicMountChildMixin_isDynamicallyMounted: false,
    canAskSalis: false,
    isAskSalisSupportedComponent: false,
    showTrashOverride: false,
    disableMedia: false,
    showSettingsOverride: null,
    showDownloadOverride: null,
    showImageAnnotation: false,
    annotation: undefined,
  },
);

const emit = defineEmits<{
  doubleClickImage: [];
  mediaPlayable: [MediaPlayablePayload];
  mediaEnded: [];
  mediaPaused: [];
  mediaLoaded: [string];
  mediaLoadedLimited: [string];
  clickImage: [Record<string, unknown>];
  isModeChanged: [Record<string, unknown>];
  editEmbed: [EditEmbedPayload];
  youTubeSettings: [YouTubeSettingsPayload];
  imageReady: [];
  remove: [];
  timestampTrigger: [TimeStampTriggerPayload];
  mediaReady: [];
  openCanvasDrawing: [Record<string, unknown>];
  openPdfInNewTab: [string];
  openPdfOverlay: [{ uuid: string; contextType?: string; contextUuid?: string }];
  canvasDrawing: [Record<string, unknown>];
  mediaSettings: [MediaSettingsPayload];
  html5MoCreated: [Record<string, unknown>];
  unapprovedMoErrorChanged: [boolean];
}>();

const player = ref<InstanceType<typeof MediaPlayer> | InstanceType<typeof YoutubePlayer> | null>(
  null,
);
const image = ref<InstanceType<typeof ImageView> | null>(null);
const transcription = ref<{ getCurrentCueText: (t: number) => string } | null>(null);
const mediaElement = ref<HTMLElement | null>(null);

const htmlSignedCookie = useHtmlSignedCookie();

const isLoading = ref(true);

const htmlBootstrapState = ref<"pending" | "ready" | "blocked">("pending");
const invalid = ref(false);
const isMediaLoaded = ref(false);
const isAnnotatable = ref(false);
const isDrawingEnabled = ref(false);
const mediaMetadata = ref<MediaMetadata>({});
const embedData = ref<EmbedConfig | null>(props.embedConfig);
const visible = ref(!props.lazyload);
const exists = ref(false);
const metadataTimeout = ref<number | undefined>(undefined);
const copyrightDisclaimer = ref("");
const timeStamp = ref<number | null>(null);
const youtubeSrcLocal = ref(props.youtubeSrc);
const cuePointsLocal = ref<CuePoints>(props.cuePoints);
const strictModeLocal = ref<boolean>(props.strictMode);
const isTranscriptEnabledLocal = ref<boolean>(props.isTranscriptEnabled);
const transcriptionFileVersionLocal = ref<string | null>(props.transcriptionFileVersion);
const renderTranscriptLocal = ref<boolean>(props.renderTranscript);
const isTrackLoaded = ref(false);
const captionSrc = ref("");
const language = ref("");
const seText = ref("");
const rootEl = ref<HTMLElement | null>(null);
const isEditingImageAnnotation = ref(false);
const mediaComponentWidth = ref(0);
// Scoped per media so multiple images annotated at once don't receive each other's events.
const imageAnnotationEventBus = new EventBus();

const { t } = useI18n();
const store = useStore();
const { showMessage } = useModal();
const { show: showToast } = useToast();
const contentSubpageInstance = useContentSubpageInstance();
const eventWatch = useEventBusWatch();
const route = useRoute();
const { actionApiFailHandler, passiveApiFailHandler } = useErrorHandler();
import {
  STAMP_ICON_URLS_TEACHER,
  STAMP_ICON_URLS_STUDENT,
} from "@modules/annotation/service/image.annotation.stamp";

const { iconType } = useResource({
  resourceDomain: props.context?.resourceDomain ?? ((props.resourceDomain ?? "") as string),
});
const { handleToggle } = usePageDrawer({
  drawerName: DRAWER_NAME.ATTEMPT,
});

const {
  hasReachedAskSalisInteractionLimits,
  isConversationSuspended,
  getAskSalisUnsupportedComponentTooltip,
} = useLessonActivityConversation();

const { isAggregateAttemptSpace } = useAttemptSpace();

const currentUserId = computed<string | number | undefined>(
  () => store.getters[`${NAMESPACE.ATTEMPT}/currentUserId`],
);
const isStudent = computed<boolean>(() => store.getters[`${NAMESPACE.AUTH}/isStudent`]);
const isCA = computed<boolean>(() => store.getters[`${NAMESPACE.AUTH}/isCA`]);
const isCO = computed<boolean>(() => store.getters[`${NAMESPACE.AUTH}/isCO`]);
const isAdminPage = computed<boolean>(
  () => store.getters[`${EDITOR_NAMESPACE.LESSON}/isAdminPage`],
);

const stampIconSelection = computed(() =>
  isStudent.value ? STAMP_ICON_URLS_STUDENT : STAMP_ICON_URLS_TEACHER,
);

const dataTitle = computed<string | null>(() => (props.isExternal ? props.altText : null));
const dataUrl = computed<string | null>(() => (props.isExternal ? props.mediaSrc : null));
const hasMoeCopyrights = computed<boolean>(
  () => !!mediaMetadata.value && mediaMetadata.value.resourceDomain === RESOURCE_DOMAIN.MOE,
);

const validationErrors = computed<Record<string, string>>(() =>
  submitLessonValidationStore
    ? { ...submitLessonValidationStore.getSubmissionValidationErrors() }
    : {},
);

const unnapprovedMOErrorLabel = computed<string>(() => {
  if (
    RESOURCE_DOMAIN.MOE.toString() === mediaMetadata.value.resourceDomain &&
    RESOURCE_STATUS.APPROVED.value !== mediaMetadata.value.status &&
    props.context &&
    props.context.pageDomain === "editor"
  ) {
    return t("media_object_not_approve_message") as unknown as string;
  }
  return t("lesson_submit_validation_unapproved_mo_error_label") as unknown as string;
});

const showUnapprovedMOErrorLabel = computed<boolean>(() => {
  if (isMediaFileNotAccessible.value) return false;

  if (
    RESOURCE_DOMAIN.MOE.toString() === mediaMetadata.value.resourceDomain &&
    RESOURCE_STATUS.APPROVED.value !== mediaMetadata.value.status &&
    props.context &&
    props.context.pageDomain === "editor"
  ) {
    return true;
  }
  let hasError = false;
  const unapprovedMOList = isEmpty(validationErrors.value.mediaObjectList)
    ? []
    : validationErrors.value.mediaObjectList.split(",");
  unapprovedMOList.forEach((unapprovedMO: string) => {
    if (unapprovedMO.split("_")[0] === props.uuid) hasError = true;
  });
  return hasError;
});

const api = computed(() => (props.isUgc ? ugcApi : MediaObjectApi));

const showCopyrightDisclaimer = computed<boolean>(() => !isEmpty(copyrightDisclaimer.value));

const copyrightTooltipLines = computed<TooltipLine[]>(() => {
  const acknowledgement = mediaMetadata.value.authorAcknowledgement?.trim() ?? "";
  const descriptions =
    mediaMetadata.value.owner === MEDIA_OBJECT_COPYRIGHT_OWNER.LICENSED_MATERIALS &&
    isDefined(mediaMetadata.value.licensorName)
      ? [`© ${mediaMetadata.value.licensorName}`, mediaMetadata.value.authorAttribution ?? ""]
      : [acknowledgement, mediaMetadata.value.authorAttribution ?? ""];
  const visibleDescriptions = descriptions.filter((description) => description.trim().length > 0);

  return visibleDescriptions.map((text, index) => ({
    text,
    showLineBreak: index < visibleDescriptions.length - 1,
  }));
});

const copyrightMessage = computed(() => {
  if (hasMoeCopyrights.value) {
    if (
      mediaMetadata.value.owner === MEDIA_OBJECT_COPYRIGHT_OWNER.LICENSED_MATERIALS &&
      isDefined(mediaMetadata.value.licensorName)
    ) {
      return `${t("media_object_copyright_label")} © ${mediaMetadata.value.licensorName}\n${mediaMetadata.value.authorAttribution ?? ""}`.trim();
    }
    return `${t("media_object_copyright_label")}\n${copyrightTooltipLines.value
      .map((line) => line.text)
      .join("\n")}`.trim();
  }
  return t("media_iframe_copyright_message") as unknown as string;
});

const aiAttributionDescription = computed<string>(
  () =>
    (isImage.value
      ? t("media_object_ai_generated_image")
      : t("media_object_ai_generated_interactive")) as unknown as string,
);

const showCopyrightPlaceHolder = computed<boolean>(
  () =>
    props.copyrightProtected && !isPrintable.value && mediaHasDisplayFrame.value && !props.isUgc,
);

const isPrintable = computed<boolean>(() => !!mediaMetadata.value?.printable);

const mediaHasDisplayFrame = computed<boolean>(
  () =>
    isImage.value ||
    isVideo.value ||
    isAudio.value ||
    isYoutube.value ||
    isHTML5.value ||
    isPdf.value ||
    isPowerpoint.value ||
    isWord.value ||
    isExcel.value ||
    isEmbed.value,
);

const annotateImageMessage = computed(() => t("media_object_annotate_image_tooltip"));
const annotateTimestampMessage = computed(() =>
  t("media_object_annotate_timestamp_tooltip", { mediaType: mediaType.value.toLowerCase() }),
);
const markCopyMessage = computed(() =>
  props.isComment
    ? t("media_object_mark_a_copy_reply_tooltip")
    : t("media_object_mark_a_copy_tooltip"),
);

const statusMessage = computed<string>(() =>
  props.isUgc ? getUgcStatusMessage() : getMOStatusMessage(),
);

const fileType = computed(() => (mediaMetadata.value.fileType ?? "").toLowerCase());
const fileStatus = computed(() => (mediaMetadata.value.fileStatus ?? "").toUpperCase());
const mediaUuid = computed<string>(() => (props.uuid ? props.uuid : ""));
const mediaType = computed<string>(() => (props.mediatype ? props.mediatype.toUpperCase() : ""));
const mediaTitle = computed(() =>
  mediaMetadata.value.fileName ? mediaMetadata.value.fileName : props.altText,
);

const isHtml5QuestionAttempt = computed(() => {
  return (
    props.html5InteractiveConfig &&
    props.html5InteractiveConfig.questionId &&
    props.html5InteractiveConfig.html5MOUuid &&
    mediaUuid.value === props.html5InteractiveConfig.html5MOUuid
  );
});

const mediaUrl = computed<string | undefined>(() => {
  let url = mediaMetadata.value.fileUrl || mediaMetadata.value.url || props.mediaSrc || "";
  if (props.type === "mo" || props.type === "ugc") {
    const timestamp = props.isComment && timeStamp.value ? timeStamp.value : Date.now();
    const separator = url.includes("?") ? "&" : "?";
    url = `${url}${separator}t=${timestamp}`;
  }
  if (isHtml5QuestionAttempt.value) {
    const endpoint = appConfigs.slsApiEndpoint + "/html5question";
    const tokenString = appLocalStorage.getItem(HTML_TOKEN_STORAGE_KEY);

    if (!tokenString) {
      passiveApiFailHandler();
      return;
    }

    let token = "";
    try {
      token = JSON.parse(tokenString);
    } catch (error) {
      console.error("Parse Json Error: " + error);
      passiveApiFailHandler();
      return;
    }

    url = `${url}&activityId=${HTML5_ACTIVITY_ID_PREFIX}${props.html5InteractiveConfig!.questionId}`;
    url = `${url}&stateId=${currentUserId.value}`;
    url = `${url}&auth=${token}`;
    url = `${url}&endpoint=${endpoint}`;
  }
  return url;
});

const cueStart = computed<number | null>(() => {
  if (isAudio.value || isVideo.value || isYoutube.value) {
    return cuePointsLocal.value && cuePointsLocal.value.start ? cuePointsLocal.value.start : 0;
  }
  return null;
});
const cueEnd = computed<number | null>(() => {
  if (isAudio.value || isVideo.value || isYoutube.value) {
    return cuePointsLocal.value && cuePointsLocal.value.end ? cuePointsLocal.value.end : 0;
  }
  return null;
});

const transcriptStart = computed<number | null>(() =>
  strictModeLocal.value ? cueStart.value : null,
);
const transcriptEnd = computed<number | null>(() => (strictModeLocal.value ? cueEnd.value : null));

const youtubePlayerData = computed<MediaInput>(() => ({
  youTubeUrl: youtubeSrcLocal.value,
  cuePoints: cuePointsLocal.value,
  strictMode: strictModeLocal.value,
}));

const mediaPlayerData = computed(() => ({
  title: mediaTitle.value,
  description: mediaMetadata.value.description,
  src: mediaUrl.value,
  originalSrcUrl: mediaMetadata.value.originalSrcUrl,
  audioOnly: isAudio.value,
  durationHint:
    isAudio.value && (mediaMetadata.value.fileDuration ?? 0) > 0
      ? (mediaMetadata.value.fileDuration ?? 0) / 1000
      : undefined,
  cuePoints: cuePointsLocal.value,
  thumbnailUrl: mediaMetadata.value.thumbnailUrl,
  hideControls: props.hideControls,
  strictMode: strictModeLocal.value,
  allowSeekOnPaused: props.allowSeekOnPaused,
  index: props.index,
  uuid: mediaUuid.value,
  mimeType: mediaMetadata.value.mimeType,
}));

const imageData = computed(() => ({
  src: mediaUrl.value,
  title: mediaTitle.value,
  description: mediaMetadata.value.description,
  dimensions: mediaMetadata.value.dimensions,
  transformed: mediaMetadata.value.transformed,
  loadOriginal: false,
  width: Number(props.width),
  height: Number(props.height),
  index: props.index,
  imageRescale: props.imageRescale,
}));

const html5Data = computed<MediaData>(() => ({
  frameAttributes: { src: mediaUrl.value } as FrameAttributes,
  src: mediaUrl.value,
  type: "html5",
}));

const frameData = computed<MediaData>(() => {
  if (isHTML5.value) return html5Data.value;

  const embedDataValue = embedData.value;
  return embedDataValue
    ? { ...embedDataValue, frameAttributes: embedDataValue.frameAttributes }
    : { frameAttributes: {} as FrameAttributes };
});

const showEmptyPlaceholder = computed<boolean>(() => showStatusMessage.value);

const showStatusMessage = computed<boolean>(
  () =>
    !isEmbed.value &&
    (isMediaFileNotAccessible.value ||
      !isMediaFileReady.value ||
      showCopyrightPlaceHolder.value ||
      !isHtmlBootstrapReady.value),
);

const isMediaFileReady = computed<boolean>(() => {
  if (isYoutube.value || isEmbed.value || props.isExternal) {
    return true;
  }
  return props.isUgc
    ? fileStatus.value === MEDIA_FILE_STATUS.READY
    : isMediaObjectFileReady(fileStatus.value, fileType.value);
});

const isMediaFileAvailableForDownload = computed<boolean>(
  () =>
    fileStatus.value === MEDIA_FILE_STATUS.READY ||
    fileStatus.value === MEDIA_FILE_STATUS.PROCESSING,
);

const isMediaFileNotAccessible = computed((): boolean => {
  if (isEmbed.value || isYoutube.value || props.isUgc || props.isExternal) {
    return false;
  }
  const moeUnapprovedOutsideMoe =
    mediaMetadata.value.resourceDomain === RESOURCE_DOMAIN.MOE.toString() &&
    RESOURCE_STATUS.APPROVED.value !== mediaMetadata.value.status &&
    !!props.context &&
    props.context.resourceDomain !== RESOURCE_DOMAIN.MOE.toString();

  // for scenario when CO/CA is in non-admin view for MOE lesson.
  // ie. without the value in the "admin" path params
  if (
    mediaMetadata.value.resourceDomain === RESOURCE_DOMAIN.MOE.toString() &&
    isEmpty(route.params.admin) &&
    !!props.context &&
    mediaMetadata.value.accessible
  ) {
    return RESOURCE_STATUS.APPROVED.value !== mediaMetadata.value.status;
  }

  return !(mediaMetadata.value && mediaMetadata.value.accessible) || moeUnapprovedOutsideMoe;
});

const isMediaFileInProcessing = computed<boolean>(
  () =>
    fileStatus.value === MEDIA_FILE_STATUS.UPLOADING ||
    fileStatus.value === MEDIA_FILE_STATUS.SCANNING ||
    fileStatus.value === MEDIA_FILE_STATUS.PROCESSING,
);

const isMediaFileInUploading = computed<boolean>(
  () => fileStatus.value === MEDIA_FILE_STATUS.UPLOADING,
);

const isImage = computed<boolean>(() => mediaType.value === MEDIA_TYPE.IMAGE);
const isAudio = computed<boolean>(() => mediaType.value === MEDIA_TYPE.AUDIO);
const isVideo = computed<boolean>(() => mediaType.value === MEDIA_TYPE.VIDEO);
const isYoutube = computed<boolean>(() => mediaType.value === MEDIA_TYPE.YOUTUBE);
const isPdf = computed<boolean>(() => mediaType.value === MEDIA_TYPE.PDF);
const isPowerpoint = computed<boolean>(() => mediaType.value === MEDIA_TYPE.POWERPOINT);
const isWord = computed<boolean>(() => mediaType.value === MEDIA_TYPE.WORD);
const isExcel = computed<boolean>(() => mediaType.value === MEDIA_TYPE.EXCEL);
const isHTML5 = computed<boolean>(() => mediaType.value === MEDIA_TYPE.HTML5);

const isHtmlBootstrapReady = computed<boolean>(
  () => !isHTML5.value || htmlBootstrapState.value === "ready",
);
const isEmbed = computed<boolean>(() => embedData.value !== null);
const isExists = computed<boolean>(() => exists.value);

const showZoomTools = computed<boolean>(
  () => props.zoomTools && mediaType.value === MEDIA_TYPE.IMAGE && isExists.value,
);

// Declared before showActionTools so it is included in that gate.
// Shows for all PDF media — no opt-in prop needed; the global pdfViewerPlugin
// provides a view-only default, overridden with capture context inside AuthoringCoPilotKnowledgeBase.
const showOpenPdfOverlay = computed<boolean>(() => isPdf.value && !isMediaFileInProcessing.value);

const showActionTools = computed<boolean>(() => {
  if (!props.actionTools) return false;
  return (
    showAskSalisButton.value ||
    showStarButton.value ||
    props.annotatable ||
    showDrawing.value ||
    showRestriction.value ||
    showDownload.value ||
    showEmbedUrl.value ||
    showOpenInNewTab.value ||
    showOpenPdfOverlay.value ||
    showTrash.value ||
    showSettings.value ||
    showAiInfoIcon.value ||
    showCopyrightIcon.value ||
    showOpenHtml5SubpageButton.value
  );
});

const showCaption = computed<boolean>(() =>
  props.caption ? showZoomTools.value || showActionTools.value || props.showTitle : false,
);

const lazyLoadMediaCss = computed<string[]>(() => [
  "lazyload",
  "media",
  mediaType.value.toLowerCase(),
]);

const mediaCss = computed(() => {
  const mediaCssObject = {
    "no-preview": nonPreviewable.value,
    embed: isEmbed.value,
    "_has-status": showStatusMessage.value,
    "_no-image": !isExists.value,
    _strict: strictModeLocal.value,
  };
  return [mediaCssObject, mediaType.value.toLowerCase()];
});

const nonPreviewable = computed<boolean>(() => {
  if (!mediaType.value) return false;
  return (
    mediaType.value === MEDIA_TYPE.EXCEL ||
    mediaType.value === MEDIA_TYPE.POWERPOINT ||
    mediaType.value === MEDIA_TYPE.WORD ||
    mediaType.value === MEDIA_TYPE.PDF ||
    mediaType.value === MEDIA_TYPE.ZIP ||
    mediaType.value === MEDIA_TYPE.OTHERS
  );
});

const showExternalSourceBanner = computed<boolean>(
  () => isEmbed.value || props.isExternal || isYoutube.value,
);

const showDrawingRestrictionBanner = computed<boolean>(() => {
  if (!props.showDrawingRestriction && !props.isInline) return false;
  if (!isImage.value) return false;
  if (!isMO.value) return false;
  if (props.isUgc || props.isExternal || isEmbed.value || isYoutube.value) return false;
  if (isStudent.value) return false;
  if (isEmpty(mediaMetadata.value)) return false;
  if (mediaMetadata.value.resourceDomain !== RESOURCE_DOMAIN.MOE) return false;
  return !isMoeResourceDrawable.value;
});

const showDrawingRestrictionTooltip = computed<boolean>(
  () => isAdminPage.value && showDrawingRestrictionBanner.value && (isCA.value || isCO.value),
);

const drawingRestrictionMessage = computed<string>(() =>
  t("media_object_drawing_restriction_banner_label"),
);

const drawingRestrictionTooltipMessage = computed<string>(() =>
  t("media_object_drawing_restriction_ca_co_tooltip"),
);

const showAiInfoIcon = computed<boolean>(
  () =>
    props.showAiInfoIcon &&
    !!mediaMetadata.value.acpGenerated &&
    (isImage.value || isHTML5.value) &&
    !props.isUgc &&
    !props.isExternal &&
    !isYoutube.value,
);

const showCopyrightIcon = computed<boolean>(() => {
  if (props.isUgc) return false;
  return props.copyright && hasMoeCopyrights.value && !showAiInfoIcon.value;
});

const showTranscript = computed<boolean>(
  () => isTranscriptEnabledLocal.value && renderTranscriptLocal.value && hasTranscript.value,
);
const hasTranscript = computed<boolean>(() => !isEmpty(captionSrc.value));

const showImageAnnotationToggle = computed<boolean>(
  () => isImage.value && props.annotatable && isExists.value && !props.isExternal,
);
const askSalisTooltipMessage = computed<string>(() => {
  if (!props.isAskSalisSupportedComponent) {
    return getAskSalisUnsupportedComponentTooltip();
  } else if (isConversationSuspended.value) {
    return t("conversation_suspended_disabled_ask_salis_tooltip");
  } else if (hasReachedAskSalisInteractionLimits.value) {
    return t("conversation_text_query_toolbar_salis_no_interactions_left_tooltip_message");
  }
  return t("attempt_drawer_attached_toolbar_conversation_tooltip");
});

const showAskSalisButton = computed<boolean>(
  () => isImage.value && !isEmpty(mediaUuid.value) && props.canAskSalis,
);
const showTimestampAnnotationToggle = computed<boolean>(
  () =>
    (isVideo.value || isAudio.value) && props.annotatable && isExists.value && !props.hasSEAudio,
);

const isMoeResourceDrawable = computed<boolean>(() => {
  const { licenseType, downloadable } = mediaMetadata.value;
  const isDrawableLicense =
    licenseType === LICENSE_TYPE.PERPETUAL.value ||
    licenseType === LICENSE_TYPE.PUBLIC_DOMAIN.value;
  return isDrawableLicense && downloadable;
});

const showDrawing = computed<boolean>(() => {
  if (
    !isDrawingEnabled.value ||
    !isImage.value ||
    !props.isInline ||
    !isMediaFileReady.value ||
    isDistributedSlideMedia.value
  ) {
    return false;
  }
  if (props.isUgc) return true;
  if (mediaMetadata.value.resourceDomain === RESOURCE_DOMAIN.MOE) {
    return props.context?.isTeacherStudentViewOrStudentView && isMoeResourceDrawable.value;
  }
  return (
    mediaMetadata.value.resourceDomain === RESOURCE_DOMAIN.PERSONAL ||
    mediaMetadata.value.resourceDomain === RESOURCE_DOMAIN.ASSIGNMENT ||
    mediaMetadata.value.resourceDomain === RESOURCE_DOMAIN.COMMUNITY
  );
});

const showRestriction = computed<boolean>(
  () =>
    !isYoutube.value &&
    !isEmbed.value &&
    !props.isExternal &&
    !isMediaFileInProcessing.value &&
    !props.isUgc &&
    (mediaMetadata.value?.availability === AVAILABILITY.RESTRICTED.value ||
      !!mediaMetadata.value?.endDate) &&
    isExists.value &&
    props.restriction,
);

const showDownload = computed(() => {
  // If override is explicitly set (not null), use it
  if (props.showDownloadOverride !== null) return props.showDownloadOverride;

  if (!isMediaFileAvailableForDownload.value) return false;
  if (props.isUgc) return true;
  if (props.isInline || isMediaFileInProcessing.value || isYoutube.value) return false;

  return !!mediaMetadata.value && !!mediaMetadata.value.downloadable;
});

const showEmbedUrl = computed<boolean>(() => props.printView && (isEmbed.value || isYoutube.value));
const showOpenHtml5SubpageButton = computed<boolean>(
  () =>
    props.isInline &&
    !!mediaMetadata.value.acpGenerated &&
    AcpHtml5Service.isAcpHtml5AppAvailable(),
);

const showOpenInNewTab = computed<boolean>(() => {
  if (!props.openInNewTab) return false;
  if (isPdf.value) return false;
  if (!isHtmlBootstrapReady.value) return false;

  return (
    !props.isUgc &&
    !isMediaFileInProcessing.value &&
    (mediaType.value === MEDIA_TYPE.HTML5 || (isEmbed.value && !invalid.value))
  );
});

const showTrash = computed<boolean>(() => props.isInline || props.showTrashOverride);
const showSettings = computed<boolean>(() => {
  // If override is explicitly set (not null), use it
  if (props.showSettingsOverride !== null) return props.showSettingsOverride;

  return (
    !props.isUgc &&
    !props.isExternal &&
    props.isInline &&
    props.context?.pageDomain === "editor" &&
    isExists.value
  );
});

const showStarButton = computed<boolean>(() => {
  if (props.context?.attemptContext?.attemptType) return false;
  if (props.isExternal || !isExists.value || isDistributedSlideMedia.value) return false;
  if (isHTML5.value) return false;
  if (props.hideStarButton) return false;
  if (props.fromMediaSettings) return false;
  return isMO.value;
});

const showMarkCopy = computed<boolean>(
  () =>
    isImage.value &&
    isMediaFileReady.value &&
    !!props.context &&
    (props.context.viewingRole === ROLE.TEACHER ||
      (props.context.viewingRole === ROLE.STUDENT && props.isComment)) &&
    (!!props.context.isTeacherStudentViewOrStudentView || isAggregateAttemptSpace.value) &&
    props.canMarkCopy &&
    (!props.copyright || !hasMoeCopyrights.value),
);

const getMarkCopyIconName = computed<string>(() => (props.isComment ? "Reply24" : "Pen24"));
const isMO = computed<boolean>(
  () => mediaType.value !== "" && mediaType.value !== MEDIA_TYPE.YOUTUBE && !props.isUgc,
);
const isDistributedSlideMedia = computed<boolean>(() => notNull(props.dataDisplayStart));
const anchor = computed<string>(() => `${props.parentAnchor ?? ""}_SE`);
const mediaAnchor = computed<string>(() => {
  if (!isEmpty(props.annotationAnchor)) {
    return props.annotationAnchor ?? "";
  }
  return props.index === ""
    ? (props.parentAnchor ?? "")
    : `${props.parentAnchor ?? ""}_${props.index}`;
});

const hasSEText = computed<boolean>(() => !isEmpty(seText.value));
const cssClasses = computed<Record<string, boolean>>(() => ({
  "has-se-annotation": hasSEText.value,
}));

watch(
  () => props.cuePoints,
  () => {
    cuePointsLocal.value = props.cuePoints ?? {};
  },
);
watch(
  mediaMetadata,
  () => {
    setTimeout(() => {
      if (props.annotatable) {
        addImageAnnotationTooltipText();
        addTimestampAnnotationTooltipText();
      }
      if (showDrawing.value) editImageTooltipText();
      if (showMarkCopy.value) addMarkCopyTooltipText();
    }, 200);
  },
  { deep: true },
);
watch(
  () => props.embedConfig,
  (value) => {
    embedData.value = value;
  },
);
watch(
  () => props.strictMode,
  () => {
    strictModeLocal.value = props.strictMode;
  },
);

watch(showUnapprovedMOErrorLabel, (val) => emit("unapprovedMoErrorChanged", val), {
  immediate: true,
});

watch(
  () => showEmptyPlaceholder.value,
  () => {
    if (showEmptyPlaceholder.value) {
      const id = props.index !== "" ? `${props.uuid}_${props.index}` : `${props.uuid}`;
      emit("mediaLoadedLimited", id);
    }
  },
  { immediate: true },
);

let mediaObserver: IntersectionObserver | undefined;
let rootResizeObserver: ResizeObserver | undefined;

onMounted(() => {
  if (rootEl.value) {
    rootResizeObserver = new ResizeObserver(([entry]) => {
      mediaComponentWidth.value = entry.contentRect.width;
    });
    rootResizeObserver.observe(rootEl.value);
  }

  eventWatch.trackWatch(
    AnnotationEventBus.$on("annotationDeactiveNoteToggle", deactivateNoteToggle),
  );
  eventWatch.trackWatch(
    SpeechEvaluationEventBus.$on("onSpeechEvaluationGraded", getSpeechEvaluationText),
  );

  if (!props.lazyload) {
    visible.value = true;
    processMedia();
    if (props.isSEGraded) {
      getSpeechEvaluationText(anchor.value);
    }
    return;
  }
  mediaObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        processMedia();
      }
    });
  });
  if (mediaElement.value) {
    mediaObserver.observe(mediaElement.value);
  }
});

onBeforeUnmount(() => {
  rootResizeObserver?.disconnect();
  if (metadataTimeout.value) {
    clearTimeout(metadataTimeout.value);
  }
});

function handleAddImageAnnotation() {
  isEditingImageAnnotation.value = true;
}

function handleDoneAddingImageAnnotation() {
  isEditingImageAnnotation.value = false;
}

function addDataTextAttribute(selector: string, title: string | unknown) {
  const tooltipElement = DOMUtils.elOf(rootEl.value as HTMLElement, selector)[0];
  if (tooltipElement) {
    tooltipElement.setAttribute("data-text", String(title));
  }
}

function addImageAnnotationTooltipText() {
  addDataTextAttribute(".note .bx--assistive-text", annotateImageMessage.value);
}
function addTimestampAnnotationTooltipText() {
  addDataTextAttribute(".timestamp .bx--assistive-text", annotateTimestampMessage.value);
}
function editImageTooltipText() {
  addDataTextAttribute(".drawing .bx--assistive-text", t("media_object_edit_image_tooltip"));
}
function addMarkCopyTooltipText() {
  addDataTextAttribute(".btn-mark-copy .bx--assistive-text", markCopyMessage.value);
}

interface PlayerPublic {
  playVideo?: () => void;
  pauseVideo?: () => void;
  getCurrentTime?: () => number | Promise<number>;
  getDuration?: () => number | Promise<number>;
  updateCueStart?: (sec: number) => void;
  updateCueEnd?: (sec: number) => void;
}

function getPlayer(): PlayerPublic {
  return (player.value as unknown as PlayerPublic) ?? {};
}

function playMedia() {
  getPlayer().playVideo?.();
}

function pauseVideo() {
  getPlayer().pauseVideo?.();
}

function getCurrentTime(): Promise<number> {
  const p = getPlayer().getCurrentTime?.();
  return Promise.resolve(p ?? 0);
}

function getDuration(): Promise<number> {
  const p = getPlayer().getDuration?.();
  return Promise.resolve(p ?? 0);
}

function updateStartTime(second: number) {
  getPlayer().updateCueStart?.(second);
}

function updateEndTime(second: number) {
  getPlayer().updateCueEnd?.(second);
}

function reportBootstrapFailure(step: "token" | "cookie", error: unknown) {
  if (step === "cookie" && !isMissingApplicationJwtError(error)) {
    htmlBootstrapState.value = "blocked";
  }

  if (isHtmlSignedCookieClearedError(error)) {
    return;
  }

  passiveApiFailHandler();
}

function processMedia() {
  visible.value = true;
  if (isYoutube.value || isEmbed.value || props.isExternal) {
    exists.value = true;
    isLoading.value = false;
    return;
  }
  if (isHTML5.value) {
    htmlBootstrapState.value = "pending";
    isLoading.value = true;
  }

  const tokenFetch = fetchHtmlToken();
  const cookieBootstrap = isHTML5.value ? htmlSignedCookie.ensure() : Promise.resolve();

  void Promise.allSettled([tokenFetch, cookieBootstrap]).then(([tokenOutcome, cookieOutcome]) => {
    if (tokenOutcome.status === "rejected") {
      reportBootstrapFailure("token", tokenOutcome.reason);
    }
    if (cookieOutcome.status === "rejected") {
      reportBootstrapFailure("cookie", cookieOutcome.reason);
    }

    if (htmlBootstrapState.value === "pending") {
      htmlBootstrapState.value = "ready";
    }
    getMetadata(0);
  });
}

async function fetchHtmlToken(): Promise<void> {
  if (!isHtml5QuestionAttempt.value) return;
  const tokenString = appLocalStorage.getItem(HTML_TOKEN_STORAGE_KEY);
  const lastFetchTime = appLocalStorage.getItem(HTML_TOKEN_LAST_FETCH_STORAGE_KEY);
  const htmlTokenFetchThreshold = 5 * 60 * 1000; // 5 minutes
  if (tokenString && lastFetchTime) {
    const now = Date.now();
    const elapsed = now - Number(lastFetchTime);
    if (elapsed < htmlTokenFetchThreshold) {
      return;
    }
  }
  return TokenApi.fetchHtmlToken()
    .then((res) => {
      if (res.code === "ok" && res.data.token) {
        appLocalStorage.setItem(HTML_TOKEN_STORAGE_KEY, res.data.token);
        appLocalStorage.setItem(HTML_TOKEN_LAST_FETCH_STORAGE_KEY, Date.now());
        return;
      }
      passiveApiFailHandler();
      return;
    })
    .catch(() => {
      // Same reason as the bootstrap handler above: the axios error object carries config.headers,
      // which on this path includes X-CSRF-TOKEN.
      console.error("Error fetching HTML5 interactive token");
      passiveApiFailHandler();
    });
}

function getMOStatusMessage(): string {
  const title = mediaTitle.value;
  let message = t("media_content_not_available") as unknown as string;
  if (isImage.value && !isExists.value) {
    message = t("media_object_image_not_found") as unknown as string;
  } else if (props.isUnavailable) {
    message = t("resource_not_available_label") as unknown as string;
  }
  if (isEmpty(mediaMetadata.value)) {
    return message;
  }
  if (showCopyrightPlaceHolder.value) {
    message = t("media_object_copyright_protected") as unknown as string;
  } else if (mediaMetadata.value?.availability === AVAILABILITY.RESTRICTED.value) {
    message = t("media_object_not_available_label", { title }) as unknown as string;
  } else if (
    !mediaMetadata.value.accessible ||
    (RESOURCE_DOMAIN.MOE.toString() === mediaMetadata.value.resourceDomain &&
      RESOURCE_STATUS.APPROVED.value !== mediaMetadata.value.status &&
      ((isEmpty(route.params.admin) && !!props.context) ||
        (props.context && props.context.resourceDomain !== RESOURCE_DOMAIN.MOE.toString())))
  ) {
    switch (fileType.value) {
      case "html5":
        message = t("resource_not_available_label") as unknown as string;
        break;
      case "audio":
        message = t("media_object_audio_not_available_label") as unknown as string;
        break;
      case "video":
        message = t("media_object_video_available_label") as unknown as string;
        break;
      case "image":
        message = t("media_object_image_not_available_label") as unknown as string;
        break;
      default:
        message = t("media_object_not_available_label", { title }) as unknown as string;
    }
  } else {
    switch (fileStatus.value) {
      case "PROCESSING":
        if (props.isSplittable) {
          message = t("split_media_processing_message") as unknown as string;
        } else if (fileType.value === "video") {
          message = t("video_transcoding_processing_label") as unknown as string;
        } else if (fileType.value === "audio") {
          message = t("audio_transcoding_processing_label") as unknown as string;
        } else {
          message = t("file_transcoding_processing_label") as unknown as string;
        }
        break;
      case "TRANSCODING_ERROR":
        if (fileType.value === "video") {
          message = t("video_trascoding_error_label") as unknown as string;
        } else {
          message = t("media_object_system_error_label") as unknown as string;
        }
        break;
      case "HTML5_UNZIP_ERROR":
      case "ERROR":
        message = t("media_object_system_error_label") as unknown as string;
        break;
      case "SCANNING":
        message = t("media_object_virus_scanning_label") as unknown as string;
        break;
      case "VIRUS":
        message = t("media_object_virus_detected_label") as unknown as string;
        break;
      case "PROTECTED":
        message = t("virus_scan_password_protected_label") as unknown as string;
        break;
      case "SIZE_EXCEEDED_ERROR":
        message = t("media_object_file_size_exceed_error_label");
        break;
    }
  }
  return message;
}

function getUgcStatusMessage(): string {
  let message = t("media_content_not_available") as unknown as string;
  if (props.isUnavailable) {
    message = t("resource_not_available_label") as unknown as string;
  }
  if (
    fileStatus.value === MEDIA_FILE_STATUS.READY &&
    (!mediaMetadata.value.accessible ||
      (RESOURCE_DOMAIN.MOE.toString() === mediaMetadata.value.resourceDomain &&
        RESOURCE_STATUS.APPROVED.value !== mediaMetadata.value.status &&
        props.context &&
        props.context.resourceDomain !== RESOURCE_DOMAIN.MOE.toString()))
  ) {
    return message;
  }
  switch (fileStatus.value) {
    case "SCANNING":
      message = t("media_object_virus_scanning_label") as unknown as string;
      break;
    case "PROCESSING":
      if (fileType.value === "video") {
        message = t("ugc_video_transcoding_processing_label") as unknown as string;
      } else if (fileType.value === "audio") {
        message = t("ugc_audio_transcoding_processing_label") as unknown as string;
      } else {
        message = t("file_transcoding_processing_label") as unknown as string;
      }
      break;
    case "TRANSCODING_ERROR":
      message = t("ugc_trascoding_error_label", {
        title: mediaMetadata.value.fileName,
      }) as unknown as string;
      break;
    case "SIZE_EXCEEDED_ERROR":
      message = t("media_object_file_size_exceed_error_label");
      break;
    default:
      message = t("ugc_system_error_label") as unknown as string;
      break;
  }
  return message;
}

function getMetadata(retryNo: number) {
  api.value
    .getMetaData({
      uuid: props.uuid,
      dataDisplayStart: props.dataDisplayStart,
      ...getApiContextParams(),
    })
    .then((res: ApiResponse<MediaMetadata>) => {
      isLoading.value = false;
      if (res.code === "error") {
        return;
      }
      mediaMetadata.value = { ...mediaMetadata.value, ...res.data };

      if (!isEmpty(props.context)) {
        MediaPlayerEventBus.$emit("updateFileDuration", {
          componentId: props.context?.componentId,
          duration: res.data.fileDuration,
        });
      } else {
        MediaPlayerEventBus.$emit("newFileDuration", {
          questionAttemptUuid: res.data.resourceUuid,
          duration: res.data.fileDuration,
        });
      }

      if (nonPreviewable.value) {
        setTimeout(() => mediaLoaded());
      }
      if (isMediaFileInProcessing.value) {
        let interval = MO_DEFAULT_FETCH_INTERVAL;
        if (retryNo < MO_FETCH_INTERVAL.length) {
          interval = MO_FETCH_INTERVAL[retryNo];
        }
        metadataTimeout.value = window.setTimeout(() => {
          getMetadata(retryNo + 1);
        }, interval);
      }

      exists.value = true;
      if (isImage.value && isMediaFileReady.value) {
        emit("imageReady");
        mediaEventBus.$emit("imageReady");
      }
      if ((isAudio.value || isVideo.value) && isMediaFileReady.value) {
        emit("mediaReady");
        mediaEventBus.$emit("mediaReady");
      }

      if ((props.isTranscriptEnabled && renderTranscriptLocal.value) || props.isFromSubpage) {
        if (api.value.getTranscriptJob) {
          api.value
            .getTranscriptJob(props.uuid, {
              params: { fileVersion: transcriptionFileVersionLocal.value ?? undefined },
            })
            .then((tRes: ApiResponse<{ transcriptFileUrl?: string; language?: string }>) => {
              if (tRes.code === "error") return;
              if (isEmpty(tRes.data)) return;
              captionSrc.value = !isEmpty(tRes.data.transcriptFileUrl)
                ? (tRes.data.transcriptFileUrl as string)
                : "";
              language.value = tRes.data.language ?? "";
            });
        }
      }
    })
    .catch((err: unknown) => {
      passiveApiFailHandler(err);
      const code = err?.response?.data?.code;
      const emap = err?.response?.data?.errorMap;
      if (code === "error" && isDefined(emap) && emap[0] === ERROR_CODE.NOT_FOUND) {
        isLoading.value = false;
      }
    });
}

function handleMediaPlayable(p: unknown) {
  const id = props.index !== "" ? `${props.uuid}_${props.index}` : `${props.uuid}`;
  emit("mediaPlayable", { player: p, id });
}
function handleMediaEnded() {
  emit("mediaEnded");
}
function handleMediaPaused() {
  emit("mediaPaused");
}

function mediaLoaded(data?: number | { copyrightDisclaimer?: string }) {
  isMediaLoaded.value = true;
  if (showTranscript.value && !isTrackLoaded.value) return;

  if (mediaType.value === MEDIA_TYPE.YOUTUBE && typeof data === "number") {
    if (props.cuePoints && !cuePointsLocal.value.start && !cuePointsLocal.value.end) {
      cuePointsLocal.value.start = 0;
      cuePointsLocal.value.end = data;
    }
  }
  const id = props.index !== "" ? `${props.uuid}_${props.index}` : `${props.uuid}`;

  if (isEmbed.value && data && typeof data === "object" && data.copyrightDisclaimer) {
    copyrightDisclaimer.value = data.copyrightDisclaimer;
  }

  emit("mediaLoaded", id);
  mediaEventBus.$emit("mediaLoaded");

  if (props.annotatable) addImageAnnotationTooltipText();
  if (props.annotatable) addTimestampAnnotationTooltipText();
}

function mediaVisible(): void {
  AnnotationEventBus.$emit("annotationRepositionImage", {
    anchor: props.parentAnchor,
    index: props.index,
  });
}

function mediaInvalid() {
  emit("mediaLoaded", props.uuid ?? "");
  invalid.value = true;
}

function mediaNotIframeable(data?: { copyrightDisclaimer?: string }) {
  if (isEmbed.value && data && data.copyrightDisclaimer) {
    copyrightDisclaimer.value = data.copyrightDisclaimer;
  }
}

function clickImage(params: Record<string, unknown>) {
  emit("clickImage", {
    ...params,
    mediaTitle: isMO.value ? (mediaMetadata.value.title ?? null) : null,
  });
}

function handleRestrictionToggle(e: MouseEvent) {
  e.preventDefault();
  const settings = {
    uuid: props.uuid,
    mediaType: mediaType.value,
    cuePoints: cuePointsLocal.value,
  };
  const component = MediaAccessRestriction;
  if (isDefined(component)) contentSubpageInstance.show(component, settings);
}

function deactivateNoteToggle(params: { anchor?: string; index?: string }) {
  const parent = DOMUtils.parent(rootEl.value as HTMLElement, ".annotatable-richtext");
  if (parent && params.anchor === (parent as HTMLElement).id && params.index === props.index) {
    isAnnotatable.value = false;
  }
}

function handleMarkCopy(e: MouseEvent) {
  e.preventDefault();
  emit("openCanvasDrawing", {
    imageUrl: mediaUrl.value,
    uuid: props.uuid,
    fileName: mediaTitle.value,
    type: props.type,
    isMarkCopy: true,
    skipCropping: true,
  });
}

function handleTimestampAnnotationToggle() {
  pauseVideo();
  let cueText: string | undefined;
  getCurrentTime().then((resp) => {
    const currentTime = Number.parseFloat(resp.toString());
    const timestamp = Math.round(currentTime * 1000);
    if (showTranscript.value) {
      cueText = transcription.value?.getCurrentCueText(currentTime);
    }
    const params: TimeStampTriggerPayload = {
      index: props.index,
      timestamp,
      highlightedContent: cueText,
      mediaTitle: isMO.value ? (mediaMetadata.value.title ?? null) : null,
      isTranscriptEnabled: isTranscriptEnabledLocal.value,
    };
    emit("timestampTrigger", params);
  });
}

function handleOpenHtml5Subpage() {
  const html5ListItem: Html5ListItem = {
    id: mediaMetadata.value.uuid as string,
    fileUrl: mediaMetadata.value.fileUrl as string,
    type: ACP_HTML5_TYPE.MO,
  };

  contentSubpageInstance.show(
    AcpHtml5InteractiveIterationContentSubpage,
    {
      baseTitle: [props.context?.lessonTitle],
      hasParentSubpage: false,
      requestId: mediaMetadata.value.uuid,
      html5List: [html5ListItem],
      richTextContext: props.context,
    },
    {
      html5MoCreated: (html5Mo: {
        uuid: string;
        fileType: string;
        fileName: string;
        type: string;
      }) => {
        const editor = props.editor;
        const richTextEditor = editor?.options.get("richTextEditor");
        const pluginEvent = editor?.options.get("pluginEvent");

        if (editor) {
          richTextUtils.insertInlineAttachment(editor, {
            uuid: html5Mo.uuid,
            mediaType: html5Mo.fileType,
            fileName: html5Mo.fileName,
            type: html5Mo.type,
            isAddNewMedia: true,
          });
          richTextUtils.renderInlineMedia(editor, ".inline-placeholder", {
            onMediaSettings: richTextEditor?.plugins["mediaObject"]?.openSettings,
          });
          editor.save();
          pluginEvent?.$emit("mediaRendered");
          showUpdateInteractiveSuccessToast();
        }
      },
    },
  );
}

function showUpdateInteractiveSuccessToast() {
  showToast("success", {
    title: t("acp_html5_interactive_update_interactive_success_toast_title") as unknown as string,
  });
}

function handleOpenInNewTab(e: MouseEvent) {
  e.preventDefault();
  if (mediaType.value === MEDIA_TYPE.HTML5) window.open(mediaUrl.value, "_blank");
  if (isEmbed.value) {
    if (embedData.value?.frameAttributes.src) {
      window.open(embedData.value.frameAttributes.src, "_blank");
    }
  }
}

function handleOpenPdfOverlayClick(e: MouseEvent) {
  e.preventDefault();
  const { contextType, contextUuid } = getApiContextParams();
  const payload = {
    uuid: props.uuid ?? "",
    filename: props.altText,
    contextType: contextType as string | undefined,
    contextUuid: contextUuid as string | undefined,
  };
  if (pdfViewerOpenFn) {
    pdfViewerOpenFn(payload);
  } else {
    // Fallback: emit for legacy parents that handle @open-pdf-overlay directly
    emit("openPdfOverlay", payload);
  }
}

function handleDownload(e: MouseEvent) {
  e.preventDefault();
  api.value
    .downloadMediaObject({ uuid: props.uuid, ...getApiContextParams() })
    .then((response: ApiResponse<string>) => {
      if (response.code === "ok") {
        performFileDownload(response.data);
      }
    })
    .catch(actionApiFailHandler);
}

function handleDraw(e: MouseEvent) {
  e.preventDefault();
  if (!isDrawingEnabled.value) return;
  emit("canvasDrawing", {
    imageUrl: mediaUrl.value,
    uuid: props.uuid,
    fileName: mediaTitle.value,
    type: props.type,
  });
}

function handleRemove() {
  if (!isEmpty(props.context)) {
    MediaPlayerEventBus.$emit("updateFileDuration", {
      componentId: props.context?.componentId,
      duration: 0,
    });
  } else {
    MediaPlayerEventBus.$emit("newFileDuration", {
      questionAttemptUuid: mediaMetadata.value.resourceUuid,
      duration: 0,
    });
  }
  emit("remove", props.uuid);
}

function handleTranscriptChange(src: string) {
  captionSrc.value = src;
}

function handleTrackLoaded() {
  isTrackLoaded.value = true;
  if (isMediaLoaded.value) {
    mediaLoaded();
  }
}

function handleSettings(e: MouseEvent) {
  e.preventDefault();
  const eventBusWatchInstances: unknown[] = [];
  eventBusWatchInstances.push(mediaEventBus.$on("settingsUpdated", updateSettings));

  let settings: Record<string, unknown> = {};

  if (isEmbed.value) {
    if (
      embedData.value?.type !== "embed-app" &&
      !isDefined(embedData.value?.frameAttributes.websiteuuid)
    ) {
      const propsModal = {
        title: t("embed_website_edit_legacy_error_title"),
        content: t("embed_website_edit_legacy_error_subtitle"),
        primaryBtnLabel: t("button_ok"),
        use_secondaryButton: false,
      };
      showMessage("warning", propsModal);
      return;
    }
    const payload: EditEmbedPayload = {
      eventBusWatchInstances,
      embedConfig: embedData.value as EmbedConfig,
    };
    emit("editEmbed", payload);
    return;
  }

  const resourceDomain = isNull(props.resourceDomain)
    ? mediaMetadata.value.resourceDomain
    : props.resourceDomain;

  switch (mediaType.value) {
    case MEDIA_TYPE.YOUTUBE: {
      const youtubePayload: YouTubeSettingsPayload = {
        eventBusWatchInstances,
        youTubeUrl: youtubeSrcLocal.value,
        cuePoints: { ...(cuePointsLocal.value as CuePoints) },
        resourceDomain: resourceDomain ?? null,
        strictMode: strictModeLocal.value,
      };
      if (isMediaFileReady.value) pauseVideo();
      emit("youTubeSettings", youtubePayload);
      break;
    }
    case MEDIA_TYPE.AUDIO:
    case MEDIA_TYPE.VIDEO: {
      settings = {
        eventBusWatchInstances,
        uuid: props.uuid,
        mediaType: mediaType.value,
        cuePoints: { ...(cuePointsLocal.value as CuePoints) },
        resourceDomain,
        isExists: isExists.value,
        isExternal: props.isExternal,
        isMO: isMO.value,
        strictMode: strictModeLocal.value,
        isTranscriptEnabled: isTranscriptEnabledLocal.value,
        transcriptionFileVersion: transcriptionFileVersionLocal.value,
        context: props.context,
        captionSrc: captionSrc.value,
        resourceType: props.context?.resourceType,
      };
      if (isMediaFileReady.value) pauseVideo();
      emit("mediaSettings", settings as MediaSettingsPayload);
      break;
    }
    default: {
      settings = {
        eventBusWatchInstances,
        uuid: props.uuid,
        mediaType: isDistributedSlideMedia.value ? MEDIA_TYPE.POWERPOINT : mediaType.value,
        cuePoints: { ...(cuePointsLocal.value as CuePoints) },
        resourceDomain,
        isExists: isExists.value,
        isExternal: props.isExternal,
        isDistributedSlideMedia: isDistributedSlideMedia.value,
        isMO: isMO.value,
        resourceType: props.context?.resourceType,
      };
      emit("mediaSettings", settings as MediaSettingsPayload);
    }
  }
}

function updateSettings(settings) {
  if (isEmbed.value) {
    if (settings.unapproved) {
      const btn = (rootEl.value as HTMLElement).querySelector(
        ".trash.action",
      ) as HTMLButtonElement | null;
      btn?.click();
      settings.unapprovedUrlCallback(settings.data.urlCode);
      return;
    }
    embedData.value = Object.assign({}, embedData.value, {
      type: settings.type,
      frameAttributes: settings.frameAttributes,
    });
    (rootEl.value as HTMLElement).parentNode
      ?.querySelector("sls-frame")
      ?.replaceWith(settings.slsFrame);
    if (settings.updateCallback) settings.updateCallback();
    return;
  }
  if (mediaType.value === MEDIA_TYPE.YOUTUBE) {
    youtubeSrcLocal.value = settings.youTubeUrl;
    cuePointsLocal.value = settings.cuePoints;
    setYoutubeStrictMode(settings.strictMode);
    nextTick(() => {
      if (player.value) {
        updateStartTime(cuePointsLocal.value.start ?? 0);
        updateEndTime(cuePointsLocal.value.end ?? 0);
      }
      if (settings.updateCallback) settings.updateCallback();
    });
  } else {
    let slsMedia: HTMLElement | null = null;
    const ele = rootEl.value as HTMLElement;
    const wrapperElement = ele.closest(".sls-media-wrapper");
    if (wrapperElement) {
      const found = DOMUtils.elOf(wrapperElement, "div.sls-media-tag")[0] as
        HTMLElement | undefined;
      slsMedia = found ?? null;
    }
    if (slsMedia) {
      if (settings?.cuePoints) {
        setMediaCuePoints(slsMedia, settings);
      }
      if (isDefined(settings?.strictMode)) {
        setStrictMode(slsMedia, settings);
      }
      setTranscriptionInfo(slsMedia, settings);
      handleTranscriptChange(settings.captionSrc);
    } else if (settings.updateCallback) {
      settings.updateCallback();
    }
  }
}

function setMediaCuePoints(slsMedia: HTMLElement, settings: { cuePoints: CuePoints }) {
  getDuration().then((response) => {
    const playerCuePoints = { ...settings.cuePoints };
    const duration = parseInt(String(response));
    if (
      (settings.cuePoints.start ?? 0) === 0 &&
      ((settings.cuePoints.end ?? 0) === 0 || (settings.cuePoints.end ?? 0) === duration)
    ) {
      playerCuePoints.start = 0;
      playerCuePoints.end = 0;
      slsMedia.removeAttribute("data-cue-start");
      slsMedia.removeAttribute("data-cue-end");
    } else {
      slsMedia.setAttribute("data-cue-start", String(settings.cuePoints.start ?? 0));
      slsMedia.setAttribute("data-cue-end", String(settings.cuePoints.end ?? 0));
    }
    cuePointsLocal.value = playerCuePoints;
  });
}

function setStrictMode(slsMedia: HTMLElement, settings: { strictMode: boolean }) {
  slsMedia.setAttribute("data-strict", String(settings.strictMode));
  strictModeLocal.value = settings.strictMode;
}

function setTranscriptionInfo(
  slsMedia: HTMLElement,
  settings: {
    transcriptionFileVersion?: string | null;
    isTranscriptEnabled?: boolean;
    captionSrc?: string;
  },
) {
  if (!isNull(settings.transcriptionFileVersion)) {
    slsMedia.setAttribute("transcription-file-version", String(settings.transcriptionFileVersion));
    transcriptionFileVersionLocal.value = settings.transcriptionFileVersion ?? null;
  }
  if (isNull(settings.isTranscriptEnabled)) return;
  slsMedia.setAttribute("data-transcript-enabled", String(settings.isTranscriptEnabled));
  isTranscriptEnabledLocal.value = !!settings.isTranscriptEnabled;
  renderTranscriptLocal.value = isTranscriptEnabledLocal.value;
  captionSrc.value = isTranscriptEnabledLocal.value ? (settings.captionSrc ?? "") : "";
}

async function getSpeechEvaluationText(targetAnchor: string) {
  if (anchor.value !== targetAnchor) {
    return;
  }

  try {
    seText.value = await getSeReferenceText({
      uuid: props.uuid,
      ...getApiContextParams(),
    });
  } catch {
    passiveApiFailHandler();
  }
}

function setYoutubeStrictMode(strictMode: boolean) {
  const youtubeComponent = (rootEl.value as HTMLElement).querySelector(
    ".media.youtube",
  ) as HTMLElement | null;
  if (youtubeComponent) {
    youtubeComponent.setAttribute("data-strict", String(strictMode));
  }
  strictModeLocal.value = strictMode;
}

function getApiContextParams(): object | { contextType?: string; contextUuid?: string } {
  if (props.isAddNewMedia) {
    return {};
  }
  if (props.isUgc) {
    if (!isEmpty(props.commentContext)) {
      if (props.commentContext?.resourceType === "QUESTION_ANSWER") {
        return { contextType: "QUESTION_ATTEMPT", contextUuid: props.commentContext?.resourceUuid };
      }
      return {};
    } else if (!isEmpty(props.questionAttemptUuid)) {
      return { contextType: "QUESTION_ATTEMPT", contextUuid: props.questionAttemptUuid };
    } else {
      return { contextType: "FORUM_TOPIC_RESOURCE", contextUuid: questionContext?.uuid };
    }
  } else {
    let isPreview = false;
    if (props.context?.attemptContext) {
      isPreview = props.context.attemptContext.attemptType === "PREVIEW";
    }
    return {
      contextType: isPreview ? "PERSONAL" : props.context?.resourceDomain,
      contextUuid:
        resourceContext?.resourceUuid ?? props.context?.assignmentUuid ?? props.context?.lessonUuid,
    };
  }
}

function enableDrawing() {
  isDrawingEnabled.value = true;
}

function buildMediaContent(): string {
  return [
    '<div class="paragraph">',
    `<div class="sls-media" name="sls-media" data-uuid="${mediaUuid.value}" data-type="${props.type}" media-type="${mediaType.value}"></div>`,
    "</div>",
    '<div class="paragraph">&nbsp;</div>',
  ].join("");
}

function handleAskSalis() {
  if (
    !props.isAskSalisSupportedComponent ||
    isConversationSuspended.value ||
    hasReachedAskSalisInteractionLimits.value
  ) {
    return;
  }
  handleToggle({ drawerContentType: DRAWER_CONTENT_TYPE.ATTEMPT.CONVERSATION, isUserAction: true });

  const content = buildMediaContent();

  ConversationDrawerEventBus.$emit("chatContextProvided", {
    anchor: mediaAnchor.value,
    content: content,
    startIndex: 0,
    endIndex: 0,
  });
}

defineExpose({
  getCurrentTime,
  getDuration,
  updateStartTime,
  updateEndTime,
  playMedia,
  pauseVideo,
  handleTranscriptChange,
  enableDrawing,
});
</script>

<style lang="scss">
@import "@styles/layout/z-map";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.media-component {
  .copyright-disclaimer {
    @include carbon--type-style("label-01", true);
    padding: $spacing-03;

    ~ .attachment-placeholder {
      border-top: $divider-02;
    }

    .rich-text-editor & {
      background-color: $ui-background;
    }
  }

  .external-source-banner,
  .drawing-restriction-banner {
    @include carbon--type-style("label-01", true);
    display: flex;
    align-items: center;
    padding: $spacing-02 $spacing-03;
    background-color: $inverse-02;
    color: $ui-01;

    .icon {
      display: inline-flex;
    }

    .bx--tooltip__trigger {
      margin-left: auto;
    }

    .sls-icon {
      [fill] {
        fill: $ui-01;
      }
    }

    .info-tooltip {
      .sls-icon {
        [fill] {
          fill: $inverse-link;
        }
      }
    }
  }

  .drawing-restriction-banner {
    gap: $spacing-03;
  }

  .tools {
    .btn-ask-salis {
      z-index: calc(
        #{z-map("default")} + 1
      ); //increase z-index to prevent tooltip message hidden behind assignment components

      &:disabled {
        opacity: 1;

        .bx--tooltip__trigger {
          cursor: not-allowed;
        }

        .icon-ai {
          [fill="#EA6613"] {
            fill: $disabled-02;
          }
        }
      }
    }
  }
}

.rich-text-editor {
  .media {
    &._no-image {
      border: none;

      .inline-loader {
        border-radius: 10px;

        ~ .attachment-placeholder {
          height: 3rem;
          border-radius: 10px;
        }
      }
    }
  }

  .mce-content-readonly {
    .attachment-placeholder {
      .tools {
        button {
          &:is(.loader, .open-in-new-tab, .copyright, .download) {
            display: flex;
          }

          display: none;
        }
      }
    }
  }
}
</style>
